import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin } from 'lucide-react';

const IngredientsOrigin = () => {
  const origins = [
    {
      location: 'Grasse, France',
      ingredient: 'Rose Centifolia',
      description: 'The perfume capital of the world, where our master perfumers craft each fragrance',
      image: 'https://images.unsplash.com/photo-1749202307928-15523cad76bf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxmcmVuY2glMjByb3NlJTIwZmllbGQlMjBwcm92ZW5jZSUyMGx1eHVyeXxlbnwwfHx8fDE3NzMwNjA0MDd8MA&ixlib=rb-4.1.0&q=75&w=500',
    },
    {
      location: 'Cambodia',
      ingredient: 'Rare Oud',
      description: 'Aged for decades in the forests of Southeast Asia for unparalleled depth',
      image: 'https://images.unsplash.com/photo-1608571423539-e951b9b3871e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxpbmNlbnNlJTIwd29vZCUyMHJlc2luJTIwYXJhYmljJTIwcGVyZnVtZXxlbnwwfHx8fDE3NzMwNjA0MzZ8MA&ixlib=rb-4.1.0&q=75&w=500',
    },
    {
      location: 'Iran',
      ingredient: 'Saffron',
      description: 'Hand-picked at dawn when the precious stigmas hold their full potency',
      image: 'https://images.unsplash.com/photo-1760982686623-16cb8449e8b0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxzYWZmcm9uJTIwY3JvY3VzJTIwZmxvd2VyJTIwaGFydmVzdHxlbnwwfHx8fDE3NzMwNjA0MTh8MA&ixlib=rb-4.1.0&q=75&w=500',
    },
    {
      location: 'Damascus, Syria',
      ingredient: 'Damask Rose',
      description: 'The queen of roses, harvested in the cool morning hours',
      image: 'https://images.unsplash.com/photo-1759665484273-e11b32943d98?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxkYW1hc2slMjByb3NlJTIwcGV0YWxzJTIwY2xvc2V1cHxlbnwwfHx8fDE3NzMwNjA0MjV8MA&ixlib=rb-4.1.0&q=75&w=500',
    },
  ];

  return (
    <section className="py-24 md:py-32 bg-[#0a0a0a] relative overflow-hidden" data-testid="ingredients-origin-section">
      <div className="absolute inset-0 geometric-pattern opacity-20" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-20 bg-gradient-to-b from-transparent via-[#D4AF37] to-transparent" />
      
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="font-arabic text-2xl text-[#D4AF37]/60 mb-2">مصادرنا</p>
          <p className="font-heading text-xs tracking-[0.3em] text-[#D4AF37] mb-4">
            SOURCED FROM THE WORLD
          </p>
          <h2 className="font-heading text-4xl md:text-5xl tracking-wide text-white">
            THE FINEST <span className="text-[#D4AF37]">INGREDIENTS</span>
          </h2>
          <p className="font-body text-white/50 mt-4 max-w-2xl mx-auto">
            We travel the globe to source only the rarest and most precious ingredients, 
            ensuring each fragrance tells a story of heritage and excellence.
          </p>
        </div>

        {/* Origins Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {origins.map((origin) => (
            <div key={origin.location} className="group relative">
              <div className="relative aspect-[3/4] overflow-hidden border border-[#D4AF37]/10 group-hover:border-[#D4AF37]/40 transition-colors duration-300">
                <img
                  src={origin.image}
                  alt={origin.ingredient}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-transparent" />
                
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin size={14} className="text-[#D4AF37]" />
                    <p className="font-heading text-xs tracking-[0.15em] text-[#D4AF37]">
                      {origin.location.toUpperCase()}
                    </p>
                  </div>
                  <h3 className="font-heading text-xl text-white mb-2">
                    {origin.ingredient}
                  </h3>
                  <p className="font-body text-white/50 text-xs leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    {origin.description}
                  </p>
                </div>

                <div className="absolute top-4 left-4 w-8 h-8 border-t border-l border-[#D4AF37]/0 group-hover:border-[#D4AF37]/50 transition-colors duration-300" />
                <div className="absolute top-4 right-4 w-8 h-8 border-t border-r border-[#D4AF37]/0 group-hover:border-[#D4AF37]/50 transition-colors duration-300" />
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <Link to="/about">
            <button className="border border-[#D4AF37]/30 text-[#D4AF37] px-8 py-4 font-heading text-xs tracking-[0.2em] hover:bg-[#D4AF37]/10 transition-colors duration-200">
              LEARN ABOUT OUR CRAFT
            </button>
          </Link>
        </div>
      </div>
      
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-px h-20 bg-gradient-to-t from-transparent via-[#D4AF37] to-transparent" />
    </section>
  );
};

export default IngredientsOrigin;
